import { useState } from "react";
import DataTable from "react-data-table-component";
import CustomLoader from "../../Components/custom/CustomLoader";
import PaginationControl from "../../Components/custom/PaginationControl";
import SearchBar from "../../Components/custom/SearchBar";
import moment from "moment";
import Unauthorized from "../../Components/custom/Unauthrized";
import Filter from "../../Components/custom/Filter";
import { useFetch, useMutate } from "../../hooks/queries";
import { BsBorder, BsFillExclamationTriangleFill } from "react-icons/bs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ListStudents = (props) => {
  const [startDate] = useState(moment().subtract(7, "d").format("YYYY-MM-DD"));
  const [endDate] = useState(moment().format("YYYY-MM-DD"));

  const [selectedSchool, setSelectedSchool] = useState({
    label: "All Schools",
    value: 0,
  });

  const [selectedOperator, setSelectedOperator] = useState({
    label: "N/A",
    value: 0,
  });

  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (column, sortDirection) => {
    // Map column selector to API field names
    const columnFieldMap = {
      canvasId: "CanvasId",
      name: "Name",
      email: "Email",
      canvasSchool: "CanvasSchool",
      schoolName: "SchoolName",
      grade: "Grade",
      lastLogin: "LastLogin",
    };

    const apiFieldName = columnFieldMap[column.id] || "";
    setSortBy(apiFieldName);
    setSortOrder(sortDirection);
    setPage(1); // Reset to first page when sorting changes
  };

  const columns = [
    {
      id: "canvasId",
      name: "Canvas Id",
      selector: (row) => row.canvasId,
      width: "150px",
      sortable: true,
      cell: (row) => {
        const schoolsMismatch = row.canvasSchool !== row.schoolName;
        return (
          <div className="d-flex align-items-center gap-2">
            <span>{row.canvasId}</span>
            {/* {schoolsMismatch && (
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id={`tooltip-mismatch-${row.canvasId}`}>
                    <div className="text-start">
                      <strong>School Mismatch</strong>
                      <br />
                      <small>
                        Canvas: {row.canvasSchool || "N/A"}
                        <br />
                        CSLP: {row.schoolName || "N/A"}
                      </small>
                    </div>
                  </Tooltip>
                }
              >
                <span style={{ color: "#ffc107", cursor: "pointer" }}>
                  <BsFillExclamationTriangleFill size={16} />
                </span>
              </OverlayTrigger>
            )} */}
          </div>
        );
      },
    },
    {
      id: "name",
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      id: "email",
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      id: "canvasSchool",
      name: "Canvas School",
      selector: (row) => row.canvasSchool,
      sortable: true,
    },
    {
      id: "schoolName",
      name: "CSLP School",
      selector: (row) => row.schoolName,
      sortable: true,
    },
    {
      id: "grade",
      name: "Grade",
      selector: (row) => row.grade,
      sortable: true,
      width: "90px",
    },
    {
      id: "section",
      name: "Section",
      selector: (row) => row.section,
      sortable: true,
      width: "110px",
    },
    {
      id: "lastLogin",
      name: "Last Login Date",
      selector: (row) =>
        row.lastLogin == null
          ? "N/A"
          : moment(row.lastLogin).format("YYYY-MM-DD"),
      sortable: true,
    },
  ];

  const handleFilterApply = ({ operator, school }) => {
    setSelectedOperator(
      operator || {
        label: "N/A",
        value: 0,
      },
    );
    setSelectedSchool(
      school || {
        label: "All Schools",
        value: 0,
      },
    );
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(1);
  };

  const buildStudentsRequestBody = () => {
    const ot = localStorage.getItem("OTsReD");
    const st = localStorage.getItem("STsReD");

    const safeParseInt = (value) => {
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parsedOt = safeParseInt(ot);
    const parsedSt = safeParseInt(st);
    const selectedOp = selectedOperator?.value;
    const selectedSch = selectedSchool?.value;

    const data = {
      OperatorId: 0,
      SchoolId: 0,
      // StartDate: startDate,
      // EndDate: endDate,
      PageNumber: page,
      PageSize: perPage,
      SearchText: searchText,
      SortBy: sortBy,
      SortOrder: sortOrder,
    };

    if (parsedOt !== 0 && parsedSt === 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = safeParseInt(selectedSch);
    } else if (parsedOt !== 0 && parsedSt !== 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = parsedSt;
    } else {
      data.OperatorId = safeParseInt(selectedOp);
      data.SchoolId = safeParseInt(selectedSch);
    }

    return data;
  };

  const {
    data: studentsResponse,
    isPending,
    isLoading,
    isFetching,
  } = useFetch({
    queryKey: [
      "students",
      page,
      perPage,
      // startDate,
      // endDate,
      selectedSchool.value,
      selectedOperator.value,
      searchText,
      sortBy,
      sortOrder,
      localStorage.getItem("OTsReD"),
      localStorage.getItem("STsReD"),
    ],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/GetStudents`,
    method: "PATCH",
    data: buildStudentsRequestBody(),
    enabled: props.userPermissions.viewStudents !== false,
  });

  const students = studentsResponse?.data || [];
  const totalRows = studentsResponse?.totalCount || 0;

  const schoolsMismatchCount = students.filter(
    (student) => student.canvasSchool !== student.schoolName,
  ).length;

  const isStudentsLoading = isPending || isLoading || isFetching;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const buildExportPayload = (filterData) => {
    const school = filterData?.school || selectedSchool;
    const operator = filterData?.operator || selectedOperator;

    return {
      // startDate: startDate,
      // endDate: endDate,
      schoolId: school.value == null ? 0 : school.value,
      operatorId: operator.value == null ? 0 : operator.value,
    };
  };

  const downloadStudentsFile = (data) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveBlob(blob, "Students.xlsx");
    } else {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "Students.xlsx");
      document.body.appendChild(link);
      link.download = "Students.xlsx";
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportStudentsMutation = useMutate({
    mutationKey: ["exportStudents"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/ExportStudents`,
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      downloadStudentsFile(data);
    },
  });

  const exportTermsMutation = useMutate({
    mutationKey: ["exportTermsReport"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/ExportTermsReport`,
    method: "GET",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      downloadStudentsFile(data);
    },
  });

  const exportSemestersMutation = useMutate({
    mutationKey: ["exportSemesterReport"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/ExportSemesterReport`,
    method: "GET",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      downloadStudentsFile(data);
    },
  });

  const viewMiddleSchoolMutation = useMutate({
    mutationKey: ["viewMiddleSchool"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/GetMiddleSchool`,
    method: "GET",
    onSuccess: (data) => {
      console.log("Middle School Data:", data);
      // TODO: Display data in a modal or process as needed
    },
  });

  const viewHighSchoolMutation = useMutate({
    mutationKey: ["viewHighSchool"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/GetHighSchool`,
    method: "GET",
    onSuccess: (data) => {
      console.log("High School Data:", data);
      // TODO: Display data in a modal or process as needed
    },
  });

  const exportingStudent =
    exportStudentsMutation.isPending || exportStudentsMutation.isLoading;
  const exportingTerms =
    exportTermsMutation.isPending || exportTermsMutation.isLoading;
  const exportingSemesters =
    exportSemestersMutation.isPending || exportSemestersMutation.isLoading;
  const viewingMiddleSchool =
    viewMiddleSchoolMutation.isPending || viewMiddleSchoolMutation.isLoading;
  const viewingHighSchool =
    viewHighSchoolMutation.isPending || viewHighSchoolMutation.isLoading;

  return (
    <>
      {props.userPermissions.viewStudents == false ? (
        <Unauthorized />
      ) : (
        <div className="content">
          <Filter
            isLoading={isStudentsLoading}
            onApply={handleFilterApply}
            canExportStudents={props.userPermissions.exportStudents}
            canExportMiddleSchool={props.userPermissions.exportMiddleSchool}
            canExportHighSchool={props.userPermissions.exportHighSchool}
            onExportStudents={(filterData) =>
              exportStudentsMutation.mutate(buildExportPayload(filterData))
            }
            onExportMiddleSchool={(filterData) =>
              exportTermsMutation.mutate(buildExportPayload(filterData))
            }
            onExportHighSchool={(filterData) =>
              exportSemestersMutation.mutate(buildExportPayload(filterData))
            }
            exportingStudent={exportingStudent}
            exportingMiddleSchool={exportingTerms}
            exportingHighSchool={exportingSemesters}
            onViewMiddleSchool={() => viewMiddleSchoolMutation.mutate()}
            onViewHighSchool={() => viewHighSchoolMutation.mutate()}
            viewingMiddleSchool={viewingMiddleSchool}
            viewingHighSchool={viewingHighSchool}
            exportButtonClassName="btn btn-sm w-100 cts-btn-primary-3"
          />

          <div className="left-side">
            <div className="container-fluid mt-30">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title d-flex align-items-center gap-2">
                    Students
                    {/* {schoolsMismatchCount > 0 && (
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip id="tooltip-mismatch-header">
                            <div className="text-start">
                              <strong>
                                {schoolsMismatchCount} School Mismatch
                                {schoolsMismatchCount !== 1 ? "es" : ""}
                              </strong>
                              <br />
                              <small>
                                Students with different Canvas and CSLP school
                                names
                              </small>
                            </div>
                          </Tooltip>
                        }
                      >
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "#ffc107",
                            color: "#000",
                            fontSize: "0.75rem",
                            padding: "0.35em 0.65em",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                          <BsFillExclamationTriangleFill size={12} />
                          {schoolsMismatchCount} Issue
                          {schoolsMismatchCount !== 1 ? "s" : ""}
                        </span>
                      </OverlayTrigger>
                    )} */}
                  </h3>
                  {/* <div style={{ minWidth: "300px", maxWidth: "400px" }}>
                    <SearchBar
                      value={searchText}
                      onSearch={handleSearch}
                      onClear={handleClearSearch}
                      placeholder="Search students..."
                      debounceMs={500}
                      disabled={isStudentsLoading}
                    />
                  </div> */}
                </div>
                <div className="card-body p-0">
                  <DataTable
                    data={students}
                    columns={columns}
                    progressPending={isStudentsLoading}
                    progressComponent={<CustomLoader />}
                    onSort={handleSort}
                    sortServer
                    fixedHeader
                    fixedHeaderScrollHeight={"65vh"}
                    defaultSortFieldId={1}
                  />
                  <PaginationControl
                    totalCount={totalRows}
                    currentPage={page}
                    pageSize={perPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePerRowsChange}
                    pageSizeOptions={[100, 200, 300, 500]}
                    disabled={isStudentsLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListStudents;
